/**
 * Active FX pool — pruning indirection for `<WeaponFxField>`.
 *
 * Current implementation: unbounded. The pool exposes one entry point
 * (`addSpawns`) and one prune step (`advance(now)`) so the eventual
 * fixed-size ring buffer (plan growth §12.4) lands as a one-file
 * change without touching the prefab call sites.
 *
 * Tracks each spawn's `expiresAt` based on the prefab's declared
 * lifetime. `advance(now)` returns the still-active list — the caller
 * (a React component) maps this into the scene tree.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { WeaponFxKind, WeaponFxSpawn } from "./types";

/** Default lifetime per FX kind, in milliseconds. */
export const FX_LIFETIME_MS: Record<WeaponFxKind, number> = {
  ballistic_tracer: 150,
  energy_beam: 400,
  missile_arc: 650,
  explosion_burst: 350,
  muzzle_flash: 100,
  shockwave_ring: 450,
};

interface ActiveFx extends WeaponFxSpawn {
  spawnedAtMs: number;
  expiresAtMs: number;
}

export interface UseFxPoolOptions {
  /** Optional cap on simultaneous active FX. When exceeded, oldest is dropped. */
  maxActive?: number;
  /** Override per-kind lifetimes. Useful for slow-mo replay or testing. */
  lifetimeMsOverride?: Partial<Record<WeaponFxKind, number>>;
}

export interface UseFxPoolApi {
  active: ReadonlyArray<ActiveFx>;
  addSpawns: (spawns: ReadonlyArray<WeaponFxSpawn>) => void;
  /** Advance the pool to the given timestamp; returns the new active count. */
  advance: (nowMs: number) => number;
  /** Reset to empty — for tests and replay-scrub-to-zero. */
  clear: () => void;
}

const DEFAULT_MAX = 32;

export function useFxPool({ maxActive = DEFAULT_MAX, lifetimeMsOverride }: UseFxPoolOptions = {}): UseFxPoolApi {
  const [active, setActive] = useState<ActiveFx[]>([]);
  const seenIdsRef = useRef<Set<number>>(new Set());

  const addSpawns = useCallback(
    (spawns: ReadonlyArray<WeaponFxSpawn>) => {
      if (spawns.length === 0) return;
      const now = performance.now();
      const fresh: ActiveFx[] = [];
      for (const s of spawns) {
        if (seenIdsRef.current.has(s.id)) continue;
        seenIdsRef.current.add(s.id);
        const base = FX_LIFETIME_MS[s.kind];
        const override = lifetimeMsOverride?.[s.kind];
        const lifetime = override ?? base;
        fresh.push({ ...s, spawnedAtMs: now, expiresAtMs: now + lifetime });
      }
      if (fresh.length === 0) return;
      setActive((prev) => {
        const next = prev.concat(fresh);
        if (next.length <= maxActive) return next;
        // Drop the oldest entries to stay under cap.
        return next.slice(next.length - maxActive);
      });
    },
    [maxActive, lifetimeMsOverride],
  );

  const advance = useCallback(
    (nowMs: number) => {
      let removed = false;
      setActive((prev) => {
        const next = prev.filter((fx) => fx.expiresAtMs > nowMs);
        if (next.length !== prev.length) removed = true;
        return next;
      });
      void removed;
      // Return value isn't stable for the same render but is useful in tests.
      return active.length;
    },
    [active.length],
  );

  const clear = useCallback(() => {
    seenIdsRef.current.clear();
    setActive([]);
  }, []);

  // Drop ids from the seen-set once their corresponding FX has expired —
  // protects against memory growth across long sessions. Runs on a
  // coarse interval since exact accuracy doesn't matter.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = performance.now();
      // The seen-set survives FX expiry on purpose (so the same id
      // doesn't re-spawn). But over the lifetime of a session we'd
      // collect millions; trim entries older than 60s.
      // ActiveFx doesn't carry the id->spawnedAt mapping after expiry
      // so we approximate: clear the whole set on the hour and accept
      // a tiny replay-restart edge.
      // (Cheaper than maintaining a parallel timestamp map.)
      if (seenIdsRef.current.size > 4096 && active.length === 0) {
        seenIdsRef.current.clear();
      }
      void now;
    }, 60_000);
    return () => clearInterval(interval);
  }, [active.length]);

  return { active, addSpawns, advance, clear };
}
